import { $$, emit } from './env';

/*
 * Chuyển màn toàn trang cho PC (thay Swiper dọc + đống handler wheel cũ).
 * - Mỗi <section data-frame id="..."> là 1 màn; id dùng làm deep link: /#tin-tuc, /#mon-phai.
 *   Link cũ ?frame=3 vẫn được hiểu (đánh số từ 1).
 * - Màn có data-frame-drawer (footer) không thay màn trước mà trượt lên đè dưới đáy.
 * - Lăn chuột/trackpad: 1 cử chỉ = 1 màn (trackpad có quán tính nên phải chờ ngắt cử chỉ mới nhận tiếp).
 * - Vùng có data-scrollable được cuộn bên trong trước, tới mép mới chuyển màn.
 */
const DURATION = 900;
const GESTURE_GAP = 180; // ms không có sự kiện wheel = cử chỉ mới
const WHEEL_THRESHOLD = 40;
const SWIPE_THRESHOLD = 60;

function isEditable(el) {
  return el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
}

function canScroll(el, dir) {
  const box = el && el.closest('[data-scrollable]');
  if (!box || box.scrollHeight <= box.clientHeight + 2) return false;
  return dir > 0 ? box.scrollTop + box.clientHeight < box.scrollHeight - 2 : box.scrollTop > 2;
}

export class Fullpage {
  constructor(root) {
    this.root = root;
    this.frames = $$('[data-frame]', root);
    this.index = -1;
    this.lockUntil = 0;
    this.lastWheel = 0;
    this.wheelSum = 0;
    this.gestureUsed = false;
  }

  init() {
    if (!this.frames.length) return;
    this.root.classList.add('is-fullpage');
    this.frames.forEach((f) => {
      f.setAttribute('inert', '');
      f.setAttribute('tabindex', '-1');
    });
    this.goTo(this.initialIndex(), { instant: true });

    window.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    document.addEventListener('keydown', (e) => this.onKey(e));
    this.bindTouch();
    document.addEventListener('click', (e) => this.onLinkClick(e));
    window.addEventListener('hashchange', () => {
      const i = this.indexOfHash(window.location.hash);
      if (i < 0) return;
      this.goTo(i);
      window.history.replaceState(null, '', window.location.pathname + window.location.search); // F5 vẫn về màn đầu
    });
    // nạp sẵn ảnh các màn còn lại khi trình duyệt rảnh, chuyển màn không phải chờ tải
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
    window.addEventListener('load', () => idle(() => this.frames.forEach((f) => f.classList.add('is-ready'))), { once: true });
  }

  get realFrames() {
    return this.frames.filter((f) => !f.hasAttribute('data-frame-drawer'));
  }

  indexOfHash(hash) {
    const id = decodeURIComponent((hash || '').replace('#', ''));
    return id ? this.frames.findIndex((f) => f.id === id && !f.hasAttribute('data-frame-drawer')) : -1;
  }

  initialIndex() {
    const byHash = this.indexOfHash(window.location.hash);
    if (byHash > -1) return byHash;
    const params = new URLSearchParams(window.location.search);
    let n = parseInt(params.get('frame'), 10);
    if (Number.isNaN(n)) {
      const key = Array.from(params.keys()).find((k) => /^frame\d+$/i.test(k));
      if (key) n = parseInt(key.replace(/\D/g, ''), 10);
    }
    return Number.isNaN(n) ? 0 : Math.min(Math.max(n - 1, 0), this.realFrames.length - 1);
  }

  step(dir) {
    this.goTo(this.index + dir);
  }

  goTo(target, { instant = false } = {}) {
    const i = Math.max(0, Math.min(this.frames.length - 1, target));
    if (i === this.index) return;
    const now = performance.now();
    if (!instant && now < this.lockUntil) return;

    const prevIndex = this.index;
    const prev = this.frames[prevIndex];
    const next = this.frames[i];
    const toDrawer = next.hasAttribute('data-frame-drawer');
    const fromDrawer = prev && prev.hasAttribute('data-frame-drawer');
    this.root.dataset.dir = i > prevIndex ? 'down' : 'up';
    this.root.classList.toggle('no-anim', instant);
    this.index = i;
    this.lockUntil = now + (instant ? 0 : DURATION);

    const wasReady = next.classList.contains('is-ready');
    next.classList.add('is-ready');
    [this.frames[i - 1], this.frames[i + 1]].forEach((f) => f && f.classList.add('is-ready'));

    const activate = () => {
      // rời footer: footer đóng lại, màn nằm dưới nó mới là màn "đang rời đi" thật
      let leaving = prev;
      if (fromDrawer) {
        prev.classList.remove('is-active');
        prev.setAttribute('inert', '');
        leaving = this.frames[prevIndex - 1];
      }
      if (toDrawer) {
        // footer: màn trước vẫn giữ nguyên, chỉ đẩy lên nhường chỗ
        this.root.classList.add('is-drawer-open');
      } else {
        this.root.classList.remove('is-drawer-open');
        if (leaving && leaving !== next) {
          leaving.classList.remove('is-active');
          leaving.classList.add('is-leaving');
          leaving.setAttribute('inert', '');
          setTimeout(() => leaving.classList.remove('is-leaving'), instant ? 0 : DURATION);
        }
      }
      next.classList.add('is-active');
      next.removeAttribute('inert');

      const current = toDrawer ? this.frames[i - 1] : next;
      this.syncLinks(current.id);
      emit('nth:frame', { index: this.realFrames.indexOf(current), id: current.id, frame: current, drawer: toDrawer });
    };

    // màn chưa từng render (content-visibility) → chờ 2 frame cho style ban đầu áp xong rồi mới chạy transition
    if (wasReady || instant) activate();
    else requestAnimationFrame(() => requestAnimationFrame(activate));
  }

  syncLinks(id) {
    $$('a[href*="#"]').forEach((a) => {
      const hash = a.getAttribute('href').split('#')[1];
      if (!hash || !this.frames.some((f) => f.id === hash)) return;
      const on = hash === id;
      a.classList.toggle('is-active', on);
      if (on) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });
  }

  onLinkClick(e) {
    const a = e.target.closest('a[href*="#"]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey) return;
    const url = new URL(a.href, window.location.href);
    const samePage = url.pathname === window.location.pathname
      || (/\/(index\.html)?$/.test(url.pathname) && /\/(index\.html)?$/.test(window.location.pathname));
    const i = this.indexOfHash(url.hash);
    if (!samePage || i < 0) return;
    e.preventDefault();
    this.lockUntil = 0;
    this.goTo(i);
    emit('nth:navigate', { id: this.frames[i].id });
  }

  onWheel(e) {
    if (document.documentElement.classList.contains('has-modal')) return;
    if (e.ctrlKey) return; // pinch-zoom trên trackpad
    const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    if (Math.abs(dy) < Math.abs(e.deltaX)) return;
    const dir = dy > 0 ? 1 : -1;
    if (canScroll(e.target, dir)) return;
    e.preventDefault();

    const now = performance.now();
    if (now - this.lastWheel > GESTURE_GAP) {
      this.wheelSum = 0;
      this.gestureUsed = false;
    }
    this.lastWheel = now;
    if (this.gestureUsed || now < this.lockUntil) return;
    this.wheelSum += dy;
    if (Math.abs(this.wheelSum) >= WHEEL_THRESHOLD) {
      this.gestureUsed = true;
      this.step(dir);
    }
  }

  onKey(e) {
    if (isEditable(e.target) || document.documentElement.classList.contains('has-modal')) return;
    const map = { ArrowDown: 1, PageDown: 1, ArrowUp: -1, PageUp: -1 };
    if (e.key in map) {
      e.preventDefault();
      this.step(map[e.key]);
    } else if (e.key === ' ' && !e.target.closest('button, a')) {
      e.preventDefault();
      this.step(e.shiftKey ? -1 : 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.goTo(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this.goTo(this.realFrames.length - 1);
    }
  }

  bindTouch() {
    // màn cảm ứng chạy layout PC (tablet ngang, laptop cảm ứng)
    let y0 = null;
    let target = null;
    window.addEventListener('touchstart', (e) => {
      y0 = e.touches.length === 1 ? e.touches[0].clientY : null;
      target = e.target;
    }, { passive: true });
    window.addEventListener('touchend', (e) => {
      if (y0 === null || document.documentElement.classList.contains('has-modal')) return;
      const dy = y0 - e.changedTouches[0].clientY;
      y0 = null;
      if (Math.abs(dy) < SWIPE_THRESHOLD) return;
      const dir = dy > 0 ? 1 : -1;
      if (!canScroll(target, dir)) this.step(dir);
    }, { passive: true });
  }
}
